import type { DomainOption } from '@shared/types/domain.types';
import { ACCOUNT_DOMAIN_STORAGE_KEY } from './accountDomain.utils';

export const DOMAIN_CATALOG_STORAGE_KEY = 'domainCatalog';

let memoryCatalog: DomainOption[] | null = null;

export function setDomainCatalog(domains: DomainOption[]): void {
  if (!domains?.length) return;
  memoryCatalog = domains;
  try {
    localStorage.setItem(DOMAIN_CATALOG_STORAGE_KEY, JSON.stringify(domains));
  } catch {
    // ignore quota errors
  }
}

function readDomainCatalog(): DomainOption[] {
  if (memoryCatalog?.length) return memoryCatalog;
  try {
    const raw = localStorage.getItem(DOMAIN_CATALOG_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DomainOption[];
    if (Array.isArray(parsed) && parsed.length > 0) {
      memoryCatalog = parsed;
      return parsed;
    }
  } catch {
    // ignore parse errors
  }
  return [];
}

function normalizeDomainKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

function findCatalogMatch(raw: string, catalog: DomainOption[]): DomainOption | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const lower = trimmed.toLowerCase();
  const compact = normalizeDomainKey(trimmed);

  return catalog.find(domain => {
    const name = domain.name?.trim() ?? '';
    const displayName = domain.displayName?.trim() ?? '';
    return (
      name.toLowerCase() === lower ||
      displayName.toLowerCase() === lower ||
      normalizeDomainKey(name) === compact ||
      normalizeDomainKey(displayName) === compact
    );
  });
}

/** User-facing label for a stored/API domain value (e.g. lifesciences -> Life Sciences). */
export function getDomainDisplayName(raw: string | null | undefined): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  if (trimmed === 'N/A') return 'N/A';

  const match = findCatalogMatch(trimmed, readDomainCatalog());
  return match?.displayName?.trim() || trimmed;
}

export function getAccountDomainDisplayName(): string | null {
  const raw = localStorage.getItem(ACCOUNT_DOMAIN_STORAGE_KEY);
  if (!raw?.trim()) return null;
  return getDomainDisplayName(raw);
}
